import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
  CreditCardIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ConfirmationModal from './ConfirmationModal';


interface PayPalPayment {
  id: number;
  member_name: string;
  amount: number;
  payment_date: string;
  cashbox_date?: string;
  accounted: number;
  event_label: string;
  abrechnungsmonat?: string;
}

interface Account {
  id: number;
  name: string;
  kontotyp: string;
}

const CARD_BG = '#1c2432';

// Avatar helpers
const getInitials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

const PayPalIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901A.641.641 0 0 1 5.577.36h6.964c2.717 0 4.617.652 5.65 1.94 1.013 1.265 1.225 2.785.611 4.687C17.84 9.515 15.5 11.7 11.547 11.7H8.46l-.62 3.93-.764 5.708z" />
  </svg>
);

const PayPalPayments: React.FC = () => {
  useContext(AuthContext);
  const [groupedPayments, setGroupedPayments] = useState<Record<string, PayPalPayment[]>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [selectedPayments, setSelectedPayments] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Record<number, boolean>>({});
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  const chartScrollRef = useRef<HTMLDivElement>(null);

  // ConfirmationModal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'success' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });

  // Bulk-Pay confirm-modal (story styled)
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  // Verfügbare Jahre abrufen
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const res = await apiClient.get('/api/drinks/paypal-payments/years');
        setAvailableYears(res.data);
      } catch (err) {
        console.error('Fehler beim Abrufen der Jahre:', err);
      }
    };
    fetchYears();
  }, []);

  // Zahlungen und Konten abrufen
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams();
        if (showAll) params.append('all', 'true');
        if (selectedYear) params.append('year', selectedYear.toString());

        const paymentsRes = await apiClient.get(`/api/drinks/paypal-payments?${params.toString()}`);
        const paymentsData = paymentsRes.data;

        const accountsRes = await apiClient.get('/api/finance/accounts');
        setAccounts(accountsRes.data);

        const grouped = paymentsData.reduce((acc: Record<string, PayPalPayment[]>, payment: PayPalPayment) => {
          const event = payment.event_label || 'Unbekanntes Event';
          if (!acc[event]) acc[event] = [];
          acc[event].push(payment);
          return acc;
        }, {});

        setGroupedPayments(grouped);
        setExpandedGroups(Object.keys(grouped).reduce((acc, event) => ({ ...acc, [event]: true }), {}));
      } catch (err: any) {
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: 'Fehler beim Laden der PayPal-Zahlungen',
          variant: 'single',
          onConfirm: () => {},
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showAll, selectedYear]);

  // Zahlung(en) als verbucht markieren
  const handleMarkAsAccounted = async (paymentIds: number[]) => {
    setProcessing(paymentIds.reduce((acc, id) => ({ ...acc, [id]: true }), {}));

    try {
      const requests = paymentIds.map((id) =>
        apiClient.put(`/api/drinks/paypal-payments/${id}/accounted`).then(() => id)
      );

      const updatedIds = await Promise.all(requests);

      const totalAmount = Object.values(groupedPayments)
        .flat()
        .filter((payment) => updatedIds.includes(payment.id))
        .reduce((sum, payment) => sum + payment.amount, 0)
        .toFixed(2);

      const kantineAccount = accounts.find((acc) => acc.name === 'Kantine');
      if (!kantineAccount) throw new Error('Kantinenkonto nicht gefunden');

      const eventSet: { [key: string]: boolean } = {};
      Object.values(groupedPayments)
        .flat()
        .filter((payment) => updatedIds.includes(payment.id))
        .forEach((payment) => { eventSet[payment.event_label] = true; });
      const events = Object.keys(eventSet).join(', ');
      const description = `Einzahlung von ${updatedIds.length} PayPal-Zahlungen (${totalAmount} EUR) für Events: ${events}`;

      const formData = new FormData();
      formData.append('type', 'deposit');
      formData.append('amount', totalAmount);
      formData.append('date', new Date().toISOString().split('T')[0]);
      formData.append('category_id', '5');
      formData.append('account_id', kantineAccount.id.toString());
      formData.append('description', description);

      await apiClient.post('/api/finance/bookings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setGroupedPayments((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((event) => {
          updated[event] = updated[event].map((payment) =>
            updatedIds.includes(payment.id)
              ? { ...payment, accounted: 1, cashbox_date: new Date().toISOString() }
              : payment
          );
        });
        return updated;
      });

      setSelectedPayments((prev) => prev.filter((id) => !updatedIds.includes(id)));

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: `${updatedIds.length} Zahlung(en) erfolgreich verbucht und ${totalAmount} EUR in die Kantine eingezahlt`,
        variant: 'single',
        onConfirm: () => {},
      });
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.message || 'Fehler beim Verbuchen der Zahlungen',
        variant: 'single',
        onConfirm: () => {},
      });
    } finally {
      setProcessing({});
    }
  };

  const toggleGroup = (event: string) => {
    setExpandedGroups((prev) => ({ ...prev, [event]: !prev[event] }));
  };

  const toggleSelectPayment = (paymentId: number) => {
    setSelectedPayments((prev) =>
      prev.includes(paymentId) ? prev.filter((id) => id !== paymentId) : [...prev, paymentId]
    );
  };

  const toggleSelectAllInGroup = (event: string) => {
    const groupPayments = groupedPayments[event].filter((p) => !p.accounted).map((p) => p.id);
    const allSelected = groupPayments.every((id) => selectedPayments.includes(id));
    if (allSelected) {
      setSelectedPayments((prev) => prev.filter((id) => !groupPayments.includes(id)));
    } else {
      setSelectedPayments((prev) => {
        const combined = [...prev, ...groupPayments];
        return combined.filter((id, index) => combined.indexOf(id) === index);
      });
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const formatDateCompact = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  const formatDayNum = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.getDate().toString().padStart(2, '0');
  };

  const formatDayShort = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '');
  };

  const formatMonthShort = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');
  };

  const monthYear = useMemo(() => {
    return new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  }, []);

  // Statistiken
  const allPayments = useMemo(() => Object.values(groupedPayments).flat(), [groupedPayments]);
  const stats = useMemo(() => ({
    total: allPayments.length,
    verbucht: allPayments.filter(p => p.accounted).length,
    offen: allPayments.filter(p => !p.accounted).length,
    summe: allPayments.reduce((sum, p) => sum + p.amount, 0),
    summeVerbucht: allPayments.filter(p => p.accounted).reduce((sum, p) => sum + p.amount, 0),
    summeOffen: allPayments.filter(p => !p.accounted).reduce((sum, p) => sum + p.amount, 0),
  }), [allPayments]);

  // Letzte Zahlung
  const lastPayment = useMemo(() => {
    if (allPayments.length === 0) return null;
    return [...allPayments].sort((a, b) => {
      const dA = new Date(a.payment_date).getTime();
      const dB = new Date(b.payment_date).getTime();
      return dB - dA;
    })[0];
  }, [allPayments]);

  // Monatliche Aggregation für Balkendiagramm (nach abrechnungsmonat, verbucht/offen)
  const monthlyData = useMemo(() => {
    const months: Record<string, { verbucht: number; offen: number }> = {};
    allPayments.forEach(p => {
      // abrechnungsmonat aus drinks_events nutzen, Fallback auf payment_date
      const dateStr = p.abrechnungsmonat || p.payment_date;
      const d = new Date(dateStr);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!months[key]) months[key] = { verbucht: 0, offen: 0 };
      if (p.accounted) {
        months[key].verbucht += p.amount;
      } else {
        months[key].offen += p.amount;
      }
    });
    const sorted = Object.entries(months).sort(([a], [b]) => a.localeCompare(b));
    const max = Math.max(...sorted.map(([, v]) => v.verbucht + v.offen), 1);
    return sorted.map(([key, { verbucht, offen }]) => ({
      label: new Date(key + '-01').toLocaleDateString('de-DE', { month: 'short' }),
      verbucht,
      offen,
      total: verbucht + offen,
      verbuchtPercent: (verbucht / max) * 100,
      offenPercent: (offen / max) * 100,
    }));
  }, [allPayments]);

  const totalAmount = useMemo(() =>
    allPayments.filter(p => selectedPayments.includes(p.id)).reduce((sum, p) => sum + p.amount, 0),
    [allPayments, selectedPayments]
  );

  // Chart nach rechts scrollen (neueste Monate sichtbar)
  useEffect(() => {
    if (chartScrollRef.current) {
      chartScrollRef.current.scrollLeft = chartScrollRef.current.scrollWidth;
    }
  }, [monthlyData]);

  if (loading) {
    return (
      <div
        className="min-h-screen font-sans text-gray-800 dark:text-slate-200"
        style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">
          <section className="pt-2 pb-6 space-y-4">
            <div className="h-3 w-48 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <div className="h-16 w-2/3 rounded animate-pulse bg-gray-100 dark:bg-white/5"  />
            <div className="h-12 w-full rounded animate-pulse bg-gray-100 dark:bg-white/[0.04]"  />
          </section>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className="rounded-2xl h-20 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Story-Snippet basierend auf echten Daten
  const renderStorySnippet = () => {
    if (stats.total === 0) {
      return (
        <>
          Noch <span className="font-bold text-gray-900 dark:text-white">keine PayPal-Zahlungen</span> für die gewählten Filter.
        </>
      );
    }
    if (stats.offen > 0) {
      return (
        <>
          <span className="font-bold text-gray-900 dark:text-white">{stats.total} Zahlungen</span> über{' '}
          <span className="font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(stats.summe)}</span>
          {' · '}
          <span className="font-bold text-amber-700 dark:text-amber-300">
            {stats.offen} offen ({formatCurrency(stats.summeOffen)})
          </span>
          {lastPayment && (
            <>
              {' · letzte Zahlung '}
              <span className="font-bold text-gray-900 dark:text-white">{formatDateCompact(lastPayment.payment_date)}</span>
            </>
          )}
          .
        </>
      );
    }
    return (
      <>
        <span className="font-bold text-gray-900 dark:text-white">{stats.total} Zahlungen</span> über{' '}
        <span className="font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(stats.summe)}</span> — alle{' '}
        <span className="font-bold text-primary-700 dark:text-primary-300">verbucht</span>
        {lastPayment && (
          <>
            {' · letzte '}
            <span className="font-bold text-gray-900 dark:text-white">{formatDateCompact(lastPayment.payment_date)}</span>
          </>
        )}
        .
      </>
    );
  };

  return (
    <div
      className="min-h-screen font-sans text-gray-800 dark:text-slate-200"
      style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">

        {/* INTRO Schriftzug */}
        <section className="pt-2 pb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-info-400/80 font-semibold mb-3">
            PayPal · Stand {monthYear}
          </div>
          <h2
            className="text-4xl md:text-6xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.02]"
            style={{ letterSpacing: '-0.04em' }}
          >
            PayPal-<br />
            <span className="text-info-600 dark:text-info-400">Konto.</span>
          </h2>
          <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
            {renderStorySnippet()}
          </p>
        </section>

        {/* Action-Banner: Offene Zahlungen */}
        {stats.offen > 0 && (
          <article
            className="rounded-3xl p-5 sm:p-6 mb-6 bg-white dark:bg-[#1c2432]"
            style={{ border: '1px solid rgba(245,158,11,0.30)', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
          >
            <div className="flex items-start gap-4 flex-wrap">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                style={{
                  background: 'rgba(245,158,11,0.15)',
                  border: '1px solid rgba(245,158,11,0.40)',
                }}
              >
                <CreditCardIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">
                  Action erforderlich
                </div>
                <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                  {stats.offen} Zahlung{stats.offen === 1 ? '' : 'en'} noch nicht verbucht
                </h4>
                <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">
                  <span className="font-bold text-amber-700 dark:text-amber-300">{formatCurrency(stats.summeOffen)}</span>{' '}
                  warten auf Einzahlung in die Kantine.
                  {selectedPayments.length > 0 && (
                    <>
                      {' '}Aktuell ausgewählt:{' '}
                      <span className="font-bold text-gray-900 dark:text-white">
                        {selectedPayments.length} ({formatCurrency(totalAmount)})
                      </span>
                      .
                    </>
                  )}
                </p>
              </div>
              {selectedPayments.length > 0 && (
                <button
                  onClick={() => setShowBulkConfirm(true)}
                  disabled={Object.values(processing).some((p) => p)}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-slate-950 text-sm font-bold transition disabled:opacity-50 hover:bg-primary-400"
                  style={{ background: '#10b981' }}
                >
                  <CheckIcon className="w-4 h-4" />
                  {selectedPayments.length} verbuchen ({formatCurrency(totalAmount)})
                </button>
              )}
            </div>
          </article>
        )}

        {/* Filter-Toolbar */}
        <section className="pt-1 pb-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm text-gray-800 dark:text-slate-200 cursor-pointer transition hover:bg-white/5 bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10"
            >
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 text-primary-500 focus:ring-primary-500"
                style={{ accentColor: '#10b981' }}
              />
              <span className="hidden sm:inline font-semibold">Auch verbuchte anzeigen</span>
              <span className="sm:hidden font-semibold">Verbuchte</span>
            </label>

            <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
              {monthlyData.length > 0 && (
                <div
                  className="hidden md:flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 flex-1 min-w-0"
                >
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mr-1 shrink-0">Trend</span>
                  <div ref={chartScrollRef} className="flex gap-1 h-6 items-end flex-1 min-w-0">
                    {monthlyData.slice(-12).map((m, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-sm group/bar relative"
                        style={{
                          height: `${Math.max(m.verbuchtPercent + m.offenPercent, 8)}%`,
                          minHeight: 4,
                          background: m.offen > 0 ? '#fbbf24' : '#34d399',
                        }}
                        title={`${m.label}: ${formatCurrency(m.total)}`}
                      />
                    ))}
                  </div>
                </div>
              )}
              <select
                value={selectedYear || ''}
                onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
                className="text-sm rounded-xl px-3 py-2.5 font-semibold text-gray-800 dark:text-slate-200 outline-none bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                style={{ colorScheme: 'dark' }}
              >
                <option value="">Alle Jahre</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Payment Groups */}
        {Object.keys(groupedPayments).length === 0 ? (
          <div
            className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
          >
            <div
              className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
              style={{
                background: 'rgba(0,112,243,0.10)',
                border: '1px solid rgba(0,112,243,0.30)',
              }}
            >
              <CreditCardIcon className="h-10 w-10 text-blue-400" />
            </div>
            <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
              Keine PayPal-Zahlungen
            </h4>
            <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-md mx-auto">
              Es gibt keine Zahlungen für die gewählten Filter. Sobald Mitglieder per PayPal
              einzahlen, erscheinen sie hier.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(groupedPayments).map(([event, payments]) => {
              const openPayments = payments.filter(p => !p.accounted);
              const totalEventAmount = payments.reduce((sum, p) => sum + p.amount, 0);
              const isExpanded = expandedGroups[event];
              const allOpenSelected = openPayments.length > 0 && openPayments.every((p) => selectedPayments.includes(p.id));

              return (
                <div
                  key={event}
                  className="rounded-2xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                >
                  {/* Group Header */}
                  <div
                    className={`flex items-center justify-between px-4 sm:px-5 py-3.5 cursor-pointer transition ${
                      isExpanded ? '' : ''
                    }`}
                    onClick={() => toggleGroup(event)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.025)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    style={{
                      borderBottom: isExpanded ? '1px solid #1e293b' : 'none',
                    }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {openPayments.length > 0 && (
                        <input
                          type="checkbox"
                          checked={allOpenSelected}
                          onChange={() => toggleSelectAllInGroup(event)}
                          className="h-4 w-4 rounded border-slate-600 flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                          style={{ accentColor: '#10b981' }}
                        />
                      )}
                      {/* Icon */}
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{
                          background: 'rgba(0,112,243,0.12)',
                          border: '1px solid rgba(0,112,243,0.35)',
                        }}
                      >
                        <PayPalIcon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">{event}</h3>
                        <p className="text-xs text-gray-600 dark:text-slate-400 mt-0.5">
                          <span className="tabular-nums">{payments.length}</span> Zahlung{payments.length === 1 ? '' : 'en'}
                          {' · '}
                          <span className="font-semibold text-gray-900 dark:text-white tabular-nums">
                            {formatCurrency(totalEventAmount)}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {openPayments.length > 0 && (
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                          style={{
                            background: 'rgba(245,158,11,0.10)',
                            border: '1px solid rgba(245,158,11,0.30)',
                            color: '#fcd34d',
                          }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                          {openPayments.length} offen
                        </span>
                      )}
                      {openPayments.length === 0 && (
                        <span
                          className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                          style={{
                            background: 'rgba(16,185,129,0.10)',
                            border: '1px solid rgba(16,185,129,0.30)',
                            color: '#86efac',
                          }}
                        >
                          <CheckIcon className="w-3 h-3" />
                          alle verbucht
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-600 dark:text-slate-400" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-600 dark:text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Payment Rows */}
                  {isExpanded && (
                    <div>
                      {payments.map((payment, idx) => {
                        const isLast = idx === payments.length - 1;
                        const initials = getInitials(payment.member_name);
                        return (
                          <div
                            key={payment.id}
                            className={`flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 transition ${
                              !isLast ? 'border-b border-gray-100 dark:border-slate-800' : ''
                            } ${
                              payment.accounted
                                ? 'bg-emerald-500/5 hover:bg-emerald-500/10'
                                : 'hover:bg-gray-50 dark:hover:bg-white/[0.03]'
                            }`}
                          >
                            {/* Checkbox / Verbucht-Indicator */}
                            <div className="shrink-0">
                              {!payment.accounted ? (
                                <input
                                  type="checkbox"
                                  checked={selectedPayments.includes(payment.id)}
                                  onChange={() => toggleSelectPayment(payment.id)}
                                  className="h-4 w-4 rounded border-slate-600"
                                  style={{ accentColor: '#10b981' }}
                                />
                              ) : (
                                <CheckCircleIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                              )}
                            </div>

                            {/* Date-Tile (PayPal-Blau) */}
                            <div
                              className="shrink-0 hidden sm:flex flex-col items-center justify-center"
                              style={{
                                width: 56,
                                height: 68,
                                borderRadius: 14,
                                background: payment.accounted
                                  ? 'linear-gradient(180deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 100%)'
                                  : 'linear-gradient(180deg, rgba(0,112,243,0.18) 0%, rgba(0,112,243,0.04) 100%)',
                                border: payment.accounted
                                  ? '1px solid rgba(16,185,129,0.25)'
                                  : '1px solid rgba(0,112,243,0.30)',
                              }}
                            >
                              <div
                                className={`text-[10px] uppercase tracking-widest font-bold ${
                                  payment.accounted ? 'text-primary-300' : 'text-blue-300'
                                }`}
                              >
                                {formatDayShort(payment.payment_date)}
                              </div>
                              <div className="text-xl font-black text-gray-900 dark:text-white leading-none mt-0.5">
                                {formatDayNum(payment.payment_date)}
                              </div>
                              <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">
                                {formatMonthShort(payment.payment_date)}
                              </div>
                            </div>

                            {/* Avatar (Mobile) */}
                            <div
                              className="sm:hidden w-9 h-9 rounded-lg font-bold flex items-center justify-center text-xs shrink-0"
                              style={{
                                background: 'rgba(0,112,243,0.15)',
                                border: '1px solid rgba(0,112,243,0.30)',
                                color: '#93c5fd',
                              }}
                            >
                              {initials}
                            </div>

                            {/* Beschreibung */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">
                                {payment.member_name}
                              </p>
                              <p className="hidden sm:block text-xs text-gray-600 dark:text-slate-400 mt-0.5">
                                {formatDate(payment.payment_date)}
                              </p>
                              <p className="sm:hidden text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">
                                {formatDateCompact(payment.payment_date)}
                              </p>
                            </div>

                            {/* Betrag color-coded */}
                            <div className="text-right shrink-0">
                              <p
                                className={`text-sm sm:text-base font-black tabular-nums ${
                                  payment.accounted ? 'text-primary-300' : 'text-white'
                                }`}
                              >
                                {formatCurrency(payment.amount)}
                              </p>
                              {payment.accounted === 1 && (
                                <p className="text-[10px] uppercase tracking-widest text-primary-600 dark:text-primary-400 font-bold mt-0.5">
                                  Verbucht
                                </p>
                              )}
                              {!payment.accounted && (
                                <p className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold mt-0.5">
                                  Eingang
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Bulk-Action-Bar (sticky bottom) */}
        {selectedPayments.length > 0 && (
          <div className="sticky bottom-4 z-20 mt-4">
            <div
              className="rounded-2xl px-5 py-3.5 flex items-center gap-3 backdrop-blur-md"
              style={{
                background: 'rgba(2,6,23,0.85)',
                border: '1px solid rgba(16,185,129,0.30)',
                boxShadow: '0 30px 60px -10px rgba(0,0,0,0.7), 0 0 0 1px rgba(16,185,129,0.2)',
              }}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-gray-900 dark:text-white">
                  {selectedPayments.length} ausgewählt
                </div>
                <div className="text-xs text-gray-600 dark:text-slate-400 tabular-nums">
                  {formatCurrency(totalAmount)} bereit zum Verbuchen
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setSelectedPayments([])}
                  className="px-3 py-2 rounded-xl text-gray-700 dark:text-slate-300 text-sm font-semibold transition hover:text-white bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowBulkConfirm(true)}
                  disabled={Object.values(processing).some((p) => p)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-slate-950 text-sm font-bold transition disabled:opacity-50"
                  style={{ background: '#10b981' }}
                >
                  <CheckIcon className="w-4 h-4" />
                  Verbuchen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk-Pay Confirm Modal (Story-Style) */}
        {showBulkConfirm && selectedPayments.length > 0 && (
          <div
            className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
            onClick={() => setShowBulkConfirm(false)}
          >
            <div
              className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-[560px] flex flex-col animate-slideUp overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}
              <div className="px-6 pt-6 pb-4 flex items-start justify-between" style={{ borderBottom: '1px solid #1e293b' }}>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-blue-700 dark:text-blue-300 font-bold">
                    PayPal · Sammel-Verbuchung
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    Zahlungen verbuchen
                  </h2>
                  <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">
                    {selectedPayments.length} Zahlung{selectedPayments.length === 1 ? '' : 'en'} in die Kantine einzahlen
                  </p>
                </div>
                <button
                  onClick={() => setShowBulkConfirm(false)}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  aria-label="Schließen"
                >
                  <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                </button>
              </div>

              {/* BODY */}
              <div className="px-6 py-5 overflow-y-auto flex-1">
                {/* Hero-Betrag */}
                <div
                  className="relative px-6 py-5 rounded-2xl overflow-hidden"
                  style={{ background: 'rgba(0,112,243,0.12)', border: '1px solid rgba(0,112,243,0.35)' }}
                >
                  <div
                    className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none blur-2xl"
                    style={{ background: 'rgba(0,112,243,0.25)' }}
                  />
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-widest text-blue-200/80 font-bold">
                      Einzahlung Kantine
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span
                        className="text-5xl font-black text-gray-900 dark:text-white leading-none tabular-nums"
                        style={{ letterSpacing: '-0.04em' }}
                      >
                        {totalAmount.toFixed(2).replace('.', ',')}
                      </span>
                      <span className="text-2xl font-black text-blue-200">€</span>
                    </div>
                    <div className="text-xs text-blue-200/80 mt-2">
                      aus {selectedPayments.length} PayPal-Zahlung{selectedPayments.length === 1 ? '' : 'en'}
                    </div>
                  </div>
                </div>

                {/* Übersicht Liste */}
                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">
                    Enthaltene Zahlungen
                  </div>
                  <div
                    className="rounded-xl max-h-44 overflow-y-auto"
                    style={{ border: '1px solid #1e293b' }}
                  >
                    {allPayments
                      .filter((p) => selectedPayments.includes(p.id))
                      .map((p, i, arr) => (
                        <div
                          key={p.id}
                          className="px-3 py-2.5 flex items-center justify-between text-sm"
                          style={{ borderBottom: i === arr.length - 1 ? 'none' : '1px solid #1e293b' }}
                        >
                          <div className="min-w-0 pr-3">
                            <div className="text-gray-800 dark:text-slate-200 font-semibold truncate">{p.member_name}</div>
                            <div className="text-[11px] text-gray-500 dark:text-slate-500 truncate">{p.event_label}</div>
                          </div>
                          <div className="text-gray-900 dark:text-white font-bold tabular-nums shrink-0">
                            {formatCurrency(p.amount)}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Hinweis */}
                <div
                  className="mt-4 px-3 py-2.5 rounded-xl flex items-start gap-2 text-xs text-gray-700 dark:text-slate-300"
                  style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.20)' }}
                >
                  <CheckCircleIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 shrink-0 mt-0.5" />
                  <span>
                    Die ausgewählten Zahlungen werden als <span className="font-bold text-primary-700 dark:text-primary-300">verbucht</span>{' '}
                    markiert und automatisch eine Einzahlung in die Kantine angelegt.
                  </span>
                </div>
              </div>

              {/* FOOTER */}
              <div
                className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
                style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
              >
                <button
                  onClick={() => setShowBulkConfirm(false)}
                  className="px-4 py-3 rounded-xl text-gray-800 dark:text-slate-200 text-sm font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => {
                    setShowBulkConfirm(false);
                    handleMarkAsAccounted(selectedPayments);
                  }}
                  disabled={Object.values(processing).some((p) => p)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-slate-950 font-bold text-sm transition disabled:opacity-50"
                  style={{ background: '#10b981' }}
                >
                  <CheckIcon className="w-4 h-4" />
                  Jetzt verbuchen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal (System-Status: success/error) */}
        <ConfirmationModal
          visible={confirmModal.isOpen}
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          type={confirmModal.type}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          button={confirmModal.variant === 'single' ? {
            text: 'OK',
            onPress: () => {
              confirmModal.onConfirm();
              setConfirmModal({ ...confirmModal, isOpen: false });
            }
          } : undefined}
          buttons={confirmModal.variant === 'two' ? {
            cancel: {
              text: 'Abbrechen',
              onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
            },
            confirm: {
              text: 'Bestätigen',
              onPress: () => {
                confirmModal.onConfirm();
                setConfirmModal({ ...confirmModal, isOpen: false });
              }
            }
          } : undefined}
        />

        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-slideUp { animation: slideUp 0.2s ease; }
        `}</style>
      </div>
    </div>
  );
};

export default PayPalPayments;
